import { Inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, filter, map, mergeMap, toArray } from 'rxjs/operators';
import { PaginatedStateEffects } from '@shared/ui';
import type {
  IAnyDevicesForDeviceTypeQuery,
  ICountDevicesByDeviceTypeQuery,
  ICountDeviceTypesQuery,
  IListDeviceTypesQuery,
  IRetrieveDeviceTypeQuery,
} from '@protocols/use-cases';
import { from, Observable, of } from 'rxjs';
import { DeviceTypeData, DeviceTypeState, DeviceTypeStateModel } from '../state/device-type.state';

export const COUNT_DEVICE_TYPES_QUERY = new InjectionToken<ICountDeviceTypesQuery>('COUNT_DEVICE_TYPES_QUERY');
export const LIST_DEVICE_TYPES_QUERY = new InjectionToken<IListDeviceTypesQuery>('LIST_DEVICE_TYPES_QUERY');
export const RETRIEVE_DEVICE_TYPE_QUERY = new InjectionToken<IRetrieveDeviceTypeQuery>('RETRIEVE_DEVICE_TYPE_QUERY');
export const COUNT_DEVICES_BY_DEVICE_TYPE_QUERY = new InjectionToken<ICountDevicesByDeviceTypeQuery>(
  'COUNT_DEVICES_BY_DEVICE_TYPE_QUERY'
);
export const ANY_DEVICES_FOR_DEVICE_TYPE_QUERY = new InjectionToken<IAnyDevicesForDeviceTypeQuery>(
  'ANY_DEVICES_FOR_DEVICE_TYPE_QUERY'
);

@Injectable()
export class DeviceTypeEffects extends PaginatedStateEffects<DeviceTypeData, DeviceTypeStateModel> {
  constructor(
    protected readonly deviceTypeState: DeviceTypeState,
    @Inject(COUNT_DEVICE_TYPES_QUERY) private readonly countDeviceTypesQuery: ICountDeviceTypesQuery,
    @Inject(LIST_DEVICE_TYPES_QUERY) private readonly listDeviceTypesQuery: IListDeviceTypesQuery,
    @Inject(RETRIEVE_DEVICE_TYPE_QUERY) private readonly retrieveDeviceTypeQuery: IRetrieveDeviceTypeQuery,
    @Inject(COUNT_DEVICES_BY_DEVICE_TYPE_QUERY)
    private readonly countDevicesByDeviceTypeQuery: ICountDevicesByDeviceTypeQuery,
    @Inject(ANY_DEVICES_FOR_DEVICE_TYPE_QUERY)
    private readonly anyDevicesForDeviceTypeQuery: IAnyDevicesForDeviceTypeQuery
  ) {
    super(deviceTypeState);
  }

  // ============================================================================
  // Backwards-compat selector aliases
  // ============================================================================

  public readonly deviceTypes$ = this.items$;
  public readonly nextDeviceTypeToken$ = this.nextToken$;
  public readonly previousDeviceTypeTokens$ = this.previousTokens$;
  public readonly deviceTypesLoaded$ = this.itemsLoaded$;
  public readonly noDeviceTypes$ = this.noItems$;

  // ============================================================================
  // Re-exposed effect aliases (for backwards compatibility)
  // ============================================================================

  public readonly deleteDeviceType = this.deleteItem;
  public readonly loadMoreDeviceTypes = this.loadMoreItems;
  public readonly initializeDeviceTypes = this.initializeItems;
  public readonly countDeviceTypes = this.countItems;

  // ============================================================================
  // Re-exposed updaters
  // ============================================================================

  public readonly setSearchTerm = this.deviceTypeState.setSearchTerm;
  public readonly changePageSize = this.deviceTypeState.changePageSize;

  // ============================================================================
  // Store-specific methods
  // ============================================================================

  public canBeDeleted(item: DeviceTypeData): Observable<boolean> {
    if (item.numDevices && item.numDevices > 0) {
      return of(false);
    } else if (item.numDevices) {
      return of(true);
    } else {
      return from(this.anyDevicesForDeviceTypeQuery.execute(item.id)).pipe(map((any) => !any));
    }
  }

  public fetchDeviceTypes(deviceTypeIds: string[]): Promise<DeviceTypeData[]> {
    return from(deviceTypeIds)
      .pipe(
        mergeMap(
          (deviceTypeId) =>
            from(this.retrieveDeviceTypeQuery.execute(deviceTypeId)).pipe(
              map((deviceType) => ({
                id: deviceType.id,
                name: deviceType.name,
                version: deviceType.version,
                numDevices: undefined,
              })),
              catchError(() => of(undefined))
            ),
          this.deviceTypeState.config.maxConcurrentCountQueries
        ),
        filter((deviceType) => deviceType !== undefined),
        toArray()
      )
      .toPromise();
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    return this.countDeviceTypesQuery.execute(searchTerm);
  }

  /**
   * Do not allow deletion of device types
   * @param itemId The ID of the item to delete
   * @returns Always false
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  protected listFlow(
    limit: number,
    lastItem: DeviceTypeData | undefined,
    searchTerm: string | undefined
  ): Observable<DeviceTypeData[]> {
    return from(
      this.listDeviceTypesQuery.execute({ searchTerm, limit, lastItemName: lastItem?.name })
    ).pipe(
      map((deviceTypes) =>
        deviceTypes.map((deviceType) => ({
          id: deviceType.id,
          name: deviceType.name,
          version: deviceType.version,
          numDevices: undefined,
        }))
      )
    );
  }

  // Override to trigger deferred count loading
  protected processDeferredAttributes(itemId: string): Observable<Partial<DeviceTypeData>> {
    return this.countAttribute(this.countDevicesByDeviceTypeQuery.execute(itemId), itemId).pipe(
      map((numDevices) => ({ numDevices }))
    );
  }
}
